/**
 * Маска российского телефона для форм заявок.
 *
 * Формат ввода — ``+7 (999) 999-99-99``: пользователь набирает только
 * цифры, а ``+7``, скобки и дефисы подставляются на лету. Все цифры
 * трактуются как часть номера РФ, ведущая ``7``/``8`` (код страны или
 * привычная «междугородняя восьмёрка») отбрасывается.
 *
 * Модуль без внешних зависимостей: чистые функции форматирования легко
 * тестировать, а DOM-обвязка живёт в ``bindPhoneMask``.
 */

export const PHONE_MASK = '+7 (999) 999-99-99';
export const PHONE_DIGITS_MAX = 10;

const PREFIX = '+7 (';
const COUNTRY_DIGITS = ['7', '8'];

function digitsOf(value) {
  return String(value ?? '').replace(/\D/g, '');
}

/**
 * Национальная часть номера — до 10 цифр без кода страны.
 *
 * Ведущая ``7``/``8`` считается кодом страны или «восьмёркой» и
 * отбрасывается; всё остальное — цифры номера.
 */
export function nationalPhoneDigits(value) {
  let digits = digitsOf(value);
  if (!digits) return '';
  if (COUNTRY_DIGITS.includes(digits[0])) digits = digits.slice(1);
  return digits.slice(0, PHONE_DIGITS_MAX);
}

/**
 * Привести произвольный ввод к виду маски.
 *
 * Пустая строка остаётся пустой. Если пользователь ввёл только код страны
 * (``7``/``8``), показываем префикс ``+7 (`` — так видно, что ввод принят.
 */
export function formatPhoneValue(value) {
  const hasDigits = digitsOf(value).length > 0;
  const digits = nationalPhoneDigits(value);
  if (!digits) return hasDigits ? PREFIX : '';

  let out = PREFIX + digits.slice(0, 3);
  if (digits.length > 3) out += ') ' + digits.slice(3, 6);
  if (digits.length > 6) out += '-' + digits.slice(6, 8);
  if (digits.length > 8) out += '-' + digits.slice(8, 10);
  return out;
}

/** Сколько цифр номера стоит слева от позиции ``caret`` во вводе. */
function nationalDigitsBefore(value, caret) {
  const head = String(value ?? '').slice(0, Math.max(0, caret));
  let digits = digitsOf(head);
  const all = digitsOf(value);
  if (all && COUNTRY_DIGITS.includes(all[0]) && digits) {
    digits = digits.slice(1);
  }
  return digits.length;
}

/** Позиция каретки после ``count`` цифр номера в отформатированной строке. */
function caretForNationalDigits(formatted, count) {
  if (count <= 0) {
    const open = formatted.indexOf('(');
    return open === -1 ? 0 : open + 1;
  }
  let seen = 0;
  for (let i = 0; i < formatted.length; i += 1) {
    if (/\d/.test(formatted[i])) {
      seen += 1;
      if (seen === count + 1) return i + 1;
    }
  }
  return formatted.length;
}

function setCaret(input, position) {
  if (typeof input.setSelectionRange === 'function') {
    input.setSelectionRange(position, position);
  }
}

/** Переформатировать поле, сохранив позицию каретки относительно цифр. */
export function applyPhoneMask(input) {
  const raw = input.value ?? '';
  const caret = typeof input.selectionStart === 'number' ? input.selectionStart : raw.length;
  const count = nationalDigitsBefore(raw, caret);
  const formatted = formatPhoneValue(raw);
  input.value = formatted;
  setCaret(input, caretForNationalDigits(formatted, count));
}

function removeDigitAt(value, index) {
  return value.slice(0, index) + value.slice(index + 1);
}

/**
 * Backspace по разделителю (``)``, `` `` , ``-``, ``(``).
 *
 * Браузер удалил бы сам разделитель, а маска тут же вернула бы его обратно —
 * поле «залипало» бы. Вместо этого удаляем цифру перед разделителем.
 * Возвращает ``true``, если событие нужно отменить (``preventDefault``).
 */
function handleBackspace(input) {
  const value = input.value ?? '';
  const start = input.selectionStart ?? value.length;
  const end = input.selectionEnd ?? start;
  if (start !== end) return false;
  const before = value.slice(0, start);
  if (!before) return false;
  if (/\d$/.test(before)) return false; // удаляем цифру — отдаём браузеру
  const lastDigit = before.search(/\d(?=\D*$)/);
  if (lastDigit === -1) return true; // остались только разделители — не трогаем их
  const count = nationalDigitsBefore(value, lastDigit);
  const formatted = formatPhoneValue(removeDigitAt(value, lastDigit));
  input.value = formatted;
  setCaret(input, caretForNationalDigits(formatted, count));
  return true;
}

/** Симметрично ``handleBackspace`` для клавиши Delete. */
function handleDelete(input) {
  const value = input.value ?? '';
  const start = input.selectionStart ?? value.length;
  const end = input.selectionEnd ?? start;
  if (start !== end) return false;
  const after = value.slice(start);
  if (!after) return false;
  if (/^\d/.test(after)) return false;
  const nextDigit = after.search(/\d/);
  if (nextDigit === -1) return true;
  const index = start + nextDigit;
  const count = nationalDigitsBefore(value, index);
  const formatted = formatPhoneValue(removeDigitAt(value, index));
  input.value = formatted;
  setCaret(input, caretForNationalDigits(formatted, count));
  return true;
}

/**
 * Подключить маску к ``<input>`` — идемпотентно.
 *
 * Вешает форматирование на ``input`` и корректное удаление по
 * ``Backspace``/``Delete``, а также сразу приводит уже заполненное поле
 * (автозаполнение, возврат по истории) к виду маски.
 */
export function bindPhoneMask(input) {
  if (!input || input.dataset?.phoneMaskBound === 'true') return;
  if (input.dataset) input.dataset.phoneMaskBound = 'true';

  if (input.value) {
    input.value = formatPhoneValue(input.value);
  }

  input.addEventListener('input', () => applyPhoneMask(input));
  input.addEventListener('keydown', (event) => {
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    if (event.key === 'Backspace') {
      if (handleBackspace(input)) event.preventDefault();
    } else if (event.key === 'Delete') {
      if (handleDelete(input)) event.preventDefault();
    }
  });
}
